import React from 'react';
import { Label } from './ui/label';
import { Layout } from 'lucide-react';

/**
 * Block width options - 5-level system matching the existing image-text-row block.
 * Exposed so renderers (PreviewRenderer) can read the same definitions.
 */
export const BLOCK_WIDTH_OPTIONS = [
  { value: 'full',      label: 'Full Width', description: 'Edge to edge (100%)' },
  { value: 'container', label: 'Container',  description: 'Matches page width (default)' },
  { value: 'large',     label: 'Large (75%)', description: 'Centered large block' },
  { value: 'medium',    label: 'Medium (60%)', description: 'Centered medium block' },
  { value: 'small',     label: 'Small (45%)', description: 'Compact / narrow reading column' },
];

/**
 * Map a blockWidth value to a Tailwind class string. PreviewRenderer reads
 * this so any block can opt in by wrapping its outer container in
 * <div className={getBlockWidthClass(content.blockWidth)}>
 */
export const getBlockWidthClass = (blockWidth) => {
  // IMPORTANT: percent-based caps (45/60/75%) make sense on desktop where
  // a "Medium" block sits centered in a wide layout. On a 360px phone, 60%
  // of viewport = 216px which forces single-character word wraps. So we
  // override the cap to FULL width below the `md` breakpoint (768px) and
  // only narrow the block back down on tablet/desktop.
  switch ((blockWidth || 'container').toLowerCase()) {
    case 'full':
      return 'w-full';
    case 'large':
      return 'mx-auto px-4 w-full md:max-w-[75%]';
    case 'medium':
      return 'mx-auto px-4 w-full md:max-w-[60%]';
    case 'small':
      // Small stays narrow earlier than Medium/Large because it's still
      // readable at ~360px on tablets, just not phones.
      return 'mx-auto px-4 w-full sm:max-w-[60%] md:max-w-[45%]';
    case 'container':
    default:
      // Same as the default container the layout already provides
      return 'mx-auto px-4 w-full max-w-7xl';
  }
};

/**
 * Reusable BlockWidthControl - drop into any block's editor panel.
 * Reads/writes `content.blockWidth`.
 */
const BlockWidthControl = ({ value = 'container', onChange, testIdPrefix = 'block-width' }) => {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Layout size={14} className="text-blue-500" />
        <Label className="text-xs font-semibold">Block Width</Label>
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {BLOCK_WIDTH_OPTIONS.map((w) => (
          <button
            key={w.value}
            type="button"
            onClick={() => onChange(w.value)}
            data-testid={`${testIdPrefix}-${w.value}`}
            title={w.description}
            className={`p-2 rounded-lg border text-[11px] font-medium transition-all ${
              (value || 'container') === w.value
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
            }`}
          >
            {w.label.replace(/\s*\(.*\)/, '')}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-slate-500">
        {BLOCK_WIDTH_OPTIONS.find((w) => w.value === (value || 'container'))?.description}
      </p>
    </div>
  );
};

export default BlockWidthControl;
